// AI Forms: every form with its status, area and last change, and "New form".

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { I } from '../../../common/v5/config';
import { createForm, listAreas, listForms } from './api';
import { LANGUAGE_NAMES, problemsFrom } from './definition';
import { Spinner, StatusBadge } from './ui';

function formatDate(value) {
	if (!value) {
		return "";
	}
	const m = moment(value);
	return m.isValid() ? m.format("YYYY-MM-DD HH:mm") : String(value);
}

function CreateDialog({ areas, areasError, onClose, onCreated }) {
	const [name, setName] = useState(I("Contact form"));
	const [language, setLanguage] = useState("sv");
	const [areaId, setAreaId] = useState(areas.length ? areas[0].id : "");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!areaId && areas.length) {
			setAreaId(areas[0].id);
		}
	}, [areas, areaId]);

	const submit = (e) => {
		e.preventDefault();
		if (!name.trim() || !areaId) {
			return;
		}
		setBusy(true);
		setError("");
		createForm({ name: name.trim(), language, areaId: Number(areaId) })
			.then(form => onCreated(form))
			.catch(err => {
				setBusy(false);
				setError(problemsFrom(err).map(p => p.message).join(" "));
			});
	};

	return (
		<div className="aif-overlay" role="dialog" aria-modal="true" aria-labelledby="aif-new-title">
			<form className="aif-dialog" onSubmit={submit}>
				<h3 id="aif-new-title">{I("New form")}</h3>
				<p className="aif-muted" style={{ marginTop: 4 }}>
					{I("It starts with one case type in the area you choose, and the description, name and email fields. You can change everything before publishing.")}
				</p>
				<label className="aif-lbl" htmlFor="aif-new-name">{I("Name")}</label>
				<input id="aif-new-name" type="text" autoFocus value={name} onChange={e => setName(e.target.value)} />
				<label className="aif-lbl" htmlFor="aif-new-area">{I("Area")}</label>
				<select id="aif-new-area" value={areaId} onChange={e => setAreaId(e.target.value)}>
					{!areas.length && <option value="">{areasError || I("No areas available")}</option>}
					{areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
				</select>
				<label className="aif-lbl" htmlFor="aif-new-lang">{I("Main language")}</label>
				<select id="aif-new-lang" value={language} onChange={e => setLanguage(e.target.value)}>
					{Object.keys(LANGUAGE_NAMES).map(code => <option key={code} value={code}>{LANGUAGE_NAMES[code]}</option>)}
				</select>
				{error && <div className="aif-error">{error}</div>}
				<div className="aif-actions">
					<button type="button" className="aif-btn" onClick={onClose}>{I("Cancel")}</button>
					<button type="submit" className="aif-btn primary" disabled={busy || !name.trim() || !areaId}>
						{busy && <Spinner />} {I("Create form")}
					</button>
				</div>
			</form>
		</div>
	);
}

export default function AIFormsList({ onOpen }) {
	const [forms, setForms] = useState(null);
	const [error, setError] = useState("");
	const [areas, setAreas] = useState([]);
	const [areasError, setAreasError] = useState("");
	const [creating, setCreating] = useState(false);

	useEffect(() => {
		let alive = true;
		listForms()
			.then(items => alive && setForms(items))
			.catch(err => alive && setError(problemsFrom(err)[0].message));
		listAreas()
			.then(items => alive && setAreas(items))
			.catch(err => alive && setAreasError(problemsFrom(err)[0].message));
		return () => { alive = false; };
	}, []);

	const areaName = (id) => {
		const a = areas.find(x => x.id === String(id));
		return a ? a.name : (id != null && id !== "" ? "#" + id : "");
	};

	const newButton = (
		<button type="button" className="aif-btn primary" onClick={() => setCreating(true)}>
			<i className="fa fa-plus" aria-hidden="true" /> {I("New form")}
		</button>
	);

	return (
		<div className="aif-page">
			<div className="aif-head">
				<div>
					<h2>{I("AI Forms")}</h2>
					<p className="aif-lead">
						{I("Contact forms for your website. While the customer types, the form can show an answer from your area's library; whatever they still send becomes an errand on the Form channel.")}
					</p>
				</div>
				{newButton}
			</div>

			{forms === null && !error && <div className="aif-center"><Spinner /></div>}
			{error && <p className="aif-error" style={{ marginTop: 16 }}>{I("The forms couldn't be loaded:")} {error}</p>}

			{forms && forms.length === 0 && (
				<div className="aif-empty">
					<i className="fa fa-list-alt fa-2x" style={{ color: "#0c87f7" }} aria-hidden="true" />
					<h3>{I("No forms yet")}</h3>
					<p className="aif-muted">{I("Create a form, choose its case types and fields, then paste one line on your website or share a link.")}</p>
					<div style={{ marginTop: 12 }}>{newButton}</div>
				</div>
			)}

			{forms && forms.length > 0 && (
				<table className="aif-table">
					<thead>
						<tr>
							<th>{I("Name")}</th>
							<th>{I("Status")}</th>
							<th>{I("Area")}</th>
							<th>{I("Last updated")}</th>
						</tr>
					</thead>
					<tbody>
						{forms.map(form => {
							const draft = form.draft || {};
							const types = (draft.case_types || []).length;
							const langs = (draft.languages || []).map(l => l.toUpperCase()).join(", ");
							return (
								<tr key={form.id} onClick={() => onOpen(form.id)}>
									<td>
										<a href="#" className="aif-name" onClick={e => { e.preventDefault(); e.stopPropagation(); onOpen(form.id); }}>
											{form.name}
										</a>
										<div className="aif-muted aif-small">
											{types === 1 ? I("1 case type") : I("{n} case types").replace("{n}", types)}
											{langs ? " · " + langs : ""}
										</div>
									</td>
									<td><StatusBadge form={form} /></td>
									<td>{areaName(form.area_id)}</td>
									<td className="aif-muted">{formatDate(form.updated_at)}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			)}

			{creating && (
				<CreateDialog
					areas={areas}
					areasError={areasError}
					onClose={() => setCreating(false)}
					onCreated={form => { setCreating(false); onOpen(form.id); }}
				/>
			)}
		</div>
	);
}
